import {
    Alert,
    Box,
    Snackbar,
    Typography
} from "@mui/material";
import { UploadCloud, X } from "lucide-react";
import { useRef, useState } from "react";

const CoverImageUpload = ({ value, onChange }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState("");

  const handleFile = (file) => {
    setFileError("");
    if (!file || !file.type.startsWith("image/")) return setFileError("Please select an image file");
    if (file.size > 5 * 1024 * 1024) {
      return setFileError("Image too large, should be less than 5MB");
    }
    const previewUrl = URL.createObjectURL(file);
    onChange({ file, previewUrl });
  };

  const handleCloseError = () => {
    setFileError("");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (value?.previewUrl) URL.revokeObjectURL(value.previewUrl);
    onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  if (value?.previewUrl) {
    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: 180,
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <img
          src={value.previewUrl}
          alt="Cover preview"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        {/* Dark overlay with edit */}
        <Box
          onClick={() => inputRef.current?.click()}
          sx={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0,
            cursor: "pointer",
            transition: "opacity 0.2s",
            "&:hover": { opacity: 1 },
          }}
        >
          <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>
            Click to change image
          </Typography>
        </Box>
        {/* Remove button */}
        <Box
          onClick={handleRemove}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            bgcolor: "rgba(0,0,0,0.55)",
            borderRadius: "50%",
            width: 30,
            height: 30,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            "&:hover": { bgcolor: "rgba(220,38,38,0.85)" },
            transition: "background 0.2s",
          }}
        >
          <X size={15} color="#fff" />
        </Box>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files[0])}
        />
      </Box>
    );
  }

  return (
    <>
      <Box
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        sx={{
          width: "100%",
          height: 180,
          borderRadius: 3,
          border: "1.5px dashed",
          borderColor: dragOver ? "primary.main" : "grey.300",
          bgcolor: dragOver ? "primary.50" : "grey.50",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": {
            borderColor: "primary.main",
            bgcolor: "primary.50",
          },
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: "primary.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <UploadCloud
            size={22}
            color="var(--mui-palette-primary-main, #1976d2)"
          />
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            Upload cover image
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Drag & drop or click to browse · JPG, PNG, WebP · Max 5MB
          </Typography>
        </Box>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files[0])}
        />
      </Box>
      <Snackbar
        open={!!fileError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          className="flex items-center"
        >
          {fileError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CoverImageUpload;